import React, { FC } from 'react'
import { makeStyles } from '@mui/styles'
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { Theme } from '@mui/material/styles'

import { IColumns, IDataItem } from '../../pages/Finance/Finance/FinanceTableLists'
import { Loader } from '../Loader'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    margin: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '& thead tr': {
      height: '50px',
    },
    '& th': {
      color: theme.palette.primary.light,
      opacity: '0.6',
      fontWeight: 400,
      fontSize: 12,
      borderColor: 'rgba(233,236,239,0.38)',
      '&:not(:first-child)': {
        textAlign: 'right',
        paddingRight: 22,
      },
    },
    '& tr': {
      display: 'table',
      tableLayout: 'fixed',
      width: '100%',
      height: '35px',
      '&:nth-child(even):not(:last-child)': {
        backgroundColor: 'rgba(216,216,216,0.05)',
      },
    },
    '& td': {
      color: theme.palette.primary.light,
      border: 'none',
      padding: '10px 16px',
      '&:not(:first-child)': {
        textAlign: 'right',
      },
    },
  },
  tableContainer: {
    width: '100%',
    maxHeight: 160,
    scrollbarWidth: 'thin',
    scrollbarColor: '#5F5F5F #4F4F4F',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#4F4F4F',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#5F5F5F',
      '&:hover': {
        backgroundColor: '#777777',
      },
      '&:active': {
        backgroundColor: '#CBCBCB',
      },
    },
  },
  tableBody: {
    maxHeight: 160,
    overflow: 'hidden',
  },
  tableFooter: {
    '& td': {
      height: 55,
      fontSize: 16,
      paddingRight: 22,
    },
  },
  notFound: {
    backgroundColor: 'transparent !important',
    '& td': {
      paddingTop: theme.spacing(6),
      paddingBottom: theme.spacing(6),
    },
  },
}))

interface TableProps {
  columns: IColumns[]
  data?: IDataItem[]
  loading?: boolean
  firstSum: number
  secondSum: number
}

export const TableFixed: FC<TableProps> = ({ columns, data, firstSum, secondSum, loading }) => {
  const classes = useStyles()
  return (
    <>
      {loading && <Loader />}

      <Paper className={classes.root}>
        <TableContainer className={classes.tableContainer}>
          <Table aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody className={classes.tableBody}>
              {data && data.length > 0 ? (
                <>
                  {data.map((row, idx) => {
                    return (
                      <TableRow key={idx}>
                        {columns.map((column) => (
                          <TableCell key={column.id}>
                            {column.format && (typeof row[column.id] === 'number' || 'string')
                              ? column.format(row[column.id])
                              : row[column.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}

                  <TableRow className={classes.tableFooter}>
                    <TableCell>Sub Total:</TableCell>
                    <TableCell>{firstSum.toFixed(3)}</TableCell>
                    {columns.length > 2 && <TableCell>{secondSum.toFixed(3)}</TableCell>}
                  </TableRow>
                </>
              ) : (
                <TableRow className={classes.notFound}>
                  <TableCell colSpan={columns.length} align={'center'}>
                    {!loading && 'No results found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  )
}
